// Company/broker details for the Rothbury "Make a Claim" form -- the
// section of that form that's the same on every claim (branch, broker,
// company, address, preferred contact, bank account). Filled in once here
// so generateClaimPdf.ts can pre-fill it on every claim's exported PDF
// instead of Janelle retyping it each time.
import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { RefreshCw, Save, Building2 } from "lucide-react";
import { DEFAULT_INSURANCE_BROKER_SETTINGS, type InsuranceBrokerSettings as BrokerSettingsShape } from "@/lib/insuranceBrokerSettings";

type Form = BrokerSettingsShape;

const SETTING_NAME_BY_KEY: Record<keyof Form, string> = {
  branch: "insurance_broker_branch",
  brokerName: "insurance_broker_name",
  clientNumber: "insurance_client_number",
  clientType: "insurance_client_type",
  companyName: "insurance_company_name",
  companyNumber: "insurance_company_number",
  address1: "insurance_address1",
  address2: "insurance_address2",
  city: "insurance_city",
  postalCode: "insurance_postal_code",
  contactName: "insurance_contact_name",
  contactRelationship: "insurance_contact_relationship",
  contactNumber: "insurance_contact_number",
  contactEmail: "insurance_contact_email",
  hasRothburyApp: "insurance_has_rothbury_app",
  bankAccountName: "insurance_bank_account_name",
  bankAccountNumber: "insurance_bank_account_number",
  brokerEmail: "insurance_broker_email",
  claimsAdviserEmail: "insurance_claims_adviser_email",
};

const LABEL_BY_KEY: Record<keyof Form, string> = {
  branch: "Branch",
  brokerName: "Your Broker Name",
  clientNumber: "Client Number",
  clientType: "Client Type",
  companyName: "Company Name",
  companyNumber: "Company Number",
  address1: "Address 1",
  address2: "Address 2",
  city: "City",
  postalCode: "Postal Code",
  contactName: "Preferred Contact Name",
  contactRelationship: "Relationship",
  contactNumber: "Contact Number",
  contactEmail: "Email Address",
  hasRothburyApp: "Do you have the Rothbury App?",
  bankAccountName: "Bank Account Name",
  bankAccountNumber: "Bank Account Number",
  brokerEmail: "Broker Email(s)",
  claimsAdviserEmail: "Claims Adviser Email(s)",
};

const InsuranceBrokerSettings: React.FC = () => {
  const [form, setForm] = useState<Form>(DEFAULT_INSURANCE_BROKER_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("settings").select("name, value").in("name", Object.values(SETTING_NAME_BY_KEY));
    setLoading(false);

    if (error) {
      toast({ title: "Insurance Broker Settings error", description: error.message, variant: "destructive" });
      return;
    }

    const byName = new Map((data || []).map((row) => [row.name, row.value]));
    const mapped = { ...DEFAULT_INSURANCE_BROKER_SETTINGS };
    (Object.keys(SETTING_NAME_BY_KEY) as (keyof Form)[]).forEach((key) => {
      const raw = byName.get(SETTING_NAME_BY_KEY[key]);
      if (raw != null) mapped[key] = raw;
    });
    setForm(mapped);
  };

  useEffect(() => {
    load();
  }, []);

  const updateField = (field: keyof Form, value: string) => setForm((f) => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true);
    const rows = (Object.keys(SETTING_NAME_BY_KEY) as (keyof Form)[]).map((key, idx) => ({
      name: SETTING_NAME_BY_KEY[key],
      value: form[key],
      category: "InsuranceBroker",
      label: LABEL_BY_KEY[key],
      type: "text",
      sort_order: (idx + 1) * 10,
      is_active: true,
    }));

    const { error } = await supabase.from("settings").upsert(rows, { onConflict: "name" });
    setSaving(false);

    if (error) {
      toast({ title: "Not saved", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Saved", description: "Insurance Broker settings updated." });
  };

  const field = (key: keyof Form, placeholder?: string) => (
    <div>
      <Label className="text-foreground">{LABEL_BY_KEY[key]}</Label>
      <Input value={form[key]} onChange={(e) => updateField(key, e.target.value)} placeholder={placeholder} className="dark-input" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Building2 className="h-5 w-5 text-yellow-500" />
            Insurance Broker Settings
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Company/broker details Rothbury's "Make a Claim" form always asks for -- filled in once here so a
            claim's PDF export pre-fills this section instead of retyping it each time.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Make a Claim</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("branch", "e.g. Wellington")}
          {field("brokerName")}
          {field("clientNumber")}
          {field("clientType")}
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Policy / Client Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("companyName")}
          {field("companyNumber")}
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Address</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("address1")}
          {field("address2")}
          {field("city")}
          {field("postalCode")}
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Preferred Contact Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("contactName")}
          {field("contactRelationship")}
          {field("contactNumber")}
          {field("contactEmail")}
          {field("hasRothburyApp", "Yes / No")}
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Email Contacts</p>
        <p className="text-xs text-muted-foreground">
          Used to pre-fill the "Email Broker" / "Email Claims Adviser" buttons on a claim. Separate multiple addresses
          with a comma.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("brokerEmail", "e.g. broker@rothbury.co.nz")}
          {field("claimsAdviserEmail", "e.g. adviser@insurer.co.nz")}
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Direct Credit Authority</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("bankAccountName")}
          {field("bankAccountNumber")}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full golden-button">
        <Save className="w-4 h-4 mr-2" />
        {saving ? "Saving Insurance Broker Settings..." : "Save Insurance Broker Settings"}
      </Button>
    </div>
  );
};

export default InsuranceBrokerSettings;
